"use client";

import { useState } from "react";
import { Plus, Edit2, Save, X } from "lucide-react";
import type { RowItem, ColumnItem, Form6Config } from "../types";

interface RowColumnManagerProps {
  tabId: number;
  rows: RowItem[];
  columns: ColumnItem[];
  config: Form6Config;
  onAddRow: (tabId: number) => void;
  onAddColumn: (tabId: number) => void;
  onDeleteRow: (rowId: number) => void;
  onDeleteColumn: (colId: number) => void;
  onUpdateRowName: (rowId: number, name: string) => void;
  onUpdateColumnName: (colId: number, name: string) => void;
}

const RowColumnManager = ({
  tabId,
  rows,
  columns,
  config,
  onAddRow,
  onAddColumn,
  onDeleteRow,
  onDeleteColumn,
  onUpdateRowName,
  onUpdateColumnName,
}: RowColumnManagerProps) => {
  const [editingRowId, setEditingRowId] = useState<number | null>(null);
  const [editingRowName, setEditingRowName] = useState<string>("");
  const [editingColumnId, setEditingColumnId] = useState<number | null>(null);
  const [editingColumnName, setEditingColumnName] = useState<string>("");

  return (
    <div className="flex flex-wrap gap-4 pb-4 border-b">
      {/* Rows */}
      <div className="flex-1 min-w-[200px]">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-gray-600 uppercase">{config.rowsLabel}</span>
          <button
            onClick={() => onAddRow(tabId)}
            className="flex items-center gap-1 text-xs px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700"
          >
            <Plus className="w-3 h-3" /> Add
          </button>
        </div>
        <div className="flex flex-wrap gap-1">
          {rows.map((row) => (
            <div
              key={row.id}
              className="flex items-center gap-1 bg-green-50 border border-green-200 px-2 py-1 rounded text-xs group"
            >
              {editingRowId === row.id ? (
                <>
                  <input
                    type="text"
                    value={editingRowName}
                    onChange={(e) => setEditingRowName(e.target.value)}
                    className="w-20 px-1 py-0.5 border rounded text-xs"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        onUpdateRowName(row.id, editingRowName);
                        setEditingRowId(null);
                      }
                      if (e.key === "Escape") setEditingRowId(null);
                    }}
                  />
                  <button
                    onClick={() => {
                      onUpdateRowName(row.id, editingRowName);
                      setEditingRowId(null);
                    }}
                    className="text-green-600"
                  >
                    <Save className="w-3 h-3" />
                  </button>
                </>
              ) : (
                <>
                  <span>{row.name}</span>
                  <button
                    onClick={() => {
                      setEditingRowId(row.id);
                      setEditingRowName(row.name);
                    }}
                    className="opacity-0 group-hover:opacity-100 text-blue-500"
                  >
                    <Edit2 className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => onDeleteRow(row.id)}
                    className="opacity-0 group-hover:opacity-100 text-red-500"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </>
              )}
            </div>
          ))}
          {rows.length === 0 && (
            <span className="text-gray-400 text-xs">No {config.rowsLabel?.toLowerCase()}</span>
          )}
        </div>
      </div>

      {/* Columns */}
      <div className="flex-1 min-w-[200px]">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-gray-600 uppercase">{config.columnsLabel}</span>
          <button
            onClick={() => onAddColumn(tabId)}
            className="flex items-center gap-1 text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            <Plus className="w-3 h-3" /> Add
          </button>
        </div>
        <div className="flex flex-wrap gap-1">
          {columns.map((col) => (
            <div
              key={col.id}
              className="flex items-center gap-1 bg-blue-50 border border-blue-200 px-2 py-1 rounded text-xs group"
            >
              {editingColumnId === col.id ? (
                <>
                  <input
                    type="text"
                    value={editingColumnName}
                    onChange={(e) => setEditingColumnName(e.target.value)}
                    className="w-16 px-1 py-0.5 border rounded text-xs"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        onUpdateColumnName(col.id, editingColumnName);
                        setEditingColumnId(null);
                      }
                      if (e.key === "Escape") setEditingColumnId(null);
                    }}
                  />
                  <button
                    onClick={() => {
                      onUpdateColumnName(col.id, editingColumnName);
                      setEditingColumnId(null);
                    }}
                    className="text-green-600"
                  >
                    <Save className="w-3 h-3" />
                  </button>
                </>
              ) : (
                <>
                  <span>{col.name}</span>
                  <button
                    onClick={() => {
                      setEditingColumnId(col.id);
                      setEditingColumnName(col.name);
                    }}
                    className="opacity-0 group-hover:opacity-100 text-blue-500"
                  >
                    <Edit2 className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => onDeleteColumn(col.id)}
                    className="opacity-0 group-hover:opacity-100 text-red-500"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </>
              )}
            </div>
          ))}
          {columns.length === 0 && (
            <span className="text-gray-400 text-xs">No {config.columnsLabel?.toLowerCase()}</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default RowColumnManager;

